import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import Pagination from "@/components/Pagination";
import { FilterBar, buildQuery } from "@/components/FilterBar";
import PageHeader, { EmptyState, fmtDate } from "@/components/PageHeader";
import { shortNo } from "@/lib/caseNo";

const VALUES = [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5];

export default function AdminMerits() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState({ district_id: "", campus_id: "", team_id: "", year: "" });
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ case_type: "merit", value: "1", description: "", status: "", conference_date: "" });

  const load = useCallback(async () => {
    const q = buildQuery(filter);
    const { data } = await api.get(`/admin/merits?${q}${q ? "&" : ""}page=${page}&page_size=25`);
    setItems(data.items); setTotal(data.total);
  }, [filter, page]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (m) => { setEditing(m); setForm({ case_type: m.case_type, value: String(m.value), description: m.description, status: m.status, conference_date: (m.conference_date || "").slice(0, 10) }); };
  const save = async () => {
    const payload = { case_type: form.case_type, value: parseInt(form.value, 10), description: form.description, status: form.status };
    if (form.conference_date) payload.conference_date = form.conference_date;
    try { await api.put(`/admin/merits/${editing.id}`, payload); toast.success("Updated"); setEditing(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Merits Management" subtitle="Global view of all merit cases"
        actions={<Button variant="outline" className="rounded-full" onClick={() => navigate("/admin/merit-presets")} data-testid="merit-options-btn"><SlidersHorizontal className="mr-2 h-4 w-4" /> Merit Options</Button>} />
      <FilterBar value={filter} onChange={(v) => { setFilter(v); setPage(1); }} />
      {items.length === 0 ? <EmptyState title="No cases" testId="admin-merits-empty" /> : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table data-testid="admin-merits-table">
            <TableHeader><TableRow className="bg-slate-50">{["Case #", "Member", "Campus", "Team", "Type", "Value", "Status", "Archived", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {items.map((m) => (
                <TableRow key={m.id} data-testid={`admin-merit-row-${m.case_number}`}>
                  <TableCell className="font-mono text-xs font-semibold">{shortNo(m.case_number)}</TableCell>
                  <TableCell className="text-sm">{m.team_member_name}</TableCell>
                  <TableCell className="text-sm">{m.campus_name}</TableCell>
                  <TableCell className="text-sm">{m.team_code}</TableCell>
                  <TableCell><StatusBadge status={m.case_type} /></TableCell>
                  <TableCell className={`font-bold tabular-nums ${m.value > 0 ? "text-emerald-600" : "text-red-600"}`}>{m.value > 0 ? `+${m.value}` : m.value}</TableCell>
                  <TableCell>
                    <StatusBadge status={m.status} />
                    {m.conference_date && (
                      <p className="mt-1 text-xs font-semibold text-blue-600" data-testid={`conference-scheduled-${m.case_number}`}>
                        Conference Scheduled for {fmtDate(m.conference_date)}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{m.archived ? "Yes" : "No"}</TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`admin-edit-merit-${m.case_number}`}>Edit</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <Pagination page={page} pageSize={25} total={total} onPage={setPage} />
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="admin-merit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit {shortNo(editing?.case_number)}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Type</Label>
                <Select value={form.case_type} onValueChange={(v) => setForm((f) => ({ ...f, case_type: v }))}>
                  <SelectTrigger data-testid="admin-merit-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="merit">Merit</SelectItem><SelectItem value="demerit">Demerit</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Value</Label>
                <Select value={form.value} onValueChange={(v) => setForm((f) => ({ ...f, value: v }))}>
                  <SelectTrigger data-testid="admin-merit-value"><SelectValue /></SelectTrigger>
                  <SelectContent>{VALUES.map((v) => <SelectItem key={v} value={String(v)}>{v > 0 ? `+${v}` : v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="admin-merit-desc" /></div>
            <div className="space-y-2"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="admin-merit-status"><SelectValue /></SelectTrigger>
                <SelectContent>{["no_action_yet", "no_further_action_needed", "conference_requested", "conference_date", "conference_completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Conference date</Label><Input type="date" value={form.conference_date} onChange={(e) => setForm((f) => ({ ...f, conference_date: e.target.value }))} data-testid="admin-merit-conf" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="admin-merit-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
